/* Agroley home page behaviour (AGRO-050). Plain custom elements, no dependencies.
   - <agroley-section>: scroll-reveal for children marked .ag-r (skipped for reduced motion).
   - <agroley-strip>:   pause/play for the scrolling highlights strip.
   - <agroley-hero>:    auto-rotating campaign carousel.
   - <agroley-range>:   auto-rotating product explorer.
   - <agroley-pop>:     seed-to-puff illustrated sequence (stage buttons, autoplay only while on screen).
   Autoplay timing is driven by the CSS progress bar (animationend), so pausing the bar pauses the clock
   and resuming continues with the remaining time. Autoplay stops for: user pause, keyboard focus inside,
   section off-screen, hidden tab, prefers-reduced-motion. Hover never pauses. Safe to load more than once. */
(() => {
  if (window.__agroleyHomeLoaded) return;
  window.__agroleyHomeLoaded = true;

  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
  const TRAVEL_MS = 700;
  const FADE_MS = 480;

  class AgroleySection extends HTMLElement {
    connectedCallback() {
      if (reduced.matches || !('IntersectionObserver' in window)) return;
      this.setAttribute('data-ready', '');
      this.io = new IntersectionObserver(
        (entries) => {
          // Very tall sections (long articles on a phone) never reach 12% visible, so also accept
          // "a good part of the screen is showing this section".
          if (entries.some((e) => e.isIntersecting && (e.intersectionRatio >= 0.12 || e.intersectionRect.height >= window.innerHeight * 0.3))) {
            this.classList.add('is-in');
            this.io.disconnect();
          }
        },
        { threshold: [0, 0.02, 0.04, 0.06, 0.08, 0.12] }
      );
      this.io.observe(this);
    }

    disconnectedCallback() {
      this.io?.disconnect();
    }
  }

  class AgroleyStrip extends HTMLElement {
    connectedCallback() {
      const root = this.querySelector('.ag-strip, .ag-ann');
      const button = this.querySelector('[data-strip-toggle]');
      if (!root || !button) return;
      button.hidden = false; // the pause button only makes sense once this script runs
      button.addEventListener('click', () => {
        const paused = root.dataset.paused !== 'true';
        root.dataset.paused = String(paused);
        button.setAttribute('aria-pressed', String(paused));
        button.setAttribute('aria-label', paused ? button.dataset.labelPlay : button.dataset.labelPause);
      });
    }
  }

  /* Shared carousel engine: items, a progress element, and visibility/focus based autoplay gating. */
  class Rotator extends HTMLElement {
    setupRotator({ root, items, progress, onChange }) {
      this.root = root;
      this.items = items;
      this.progress = progress;
      this.onChange = onChange;
      this.index = Math.max(0, items.findIndex((el) => el.dataset.state === 'active'));
      this.userPaused = false;
      this.focused = false;
      this.visible = false;
      this.tabVisible = !document.hidden;
      this.motion = !reduced.matches;
      this.busy = false;
      this.timers = new Set();

      root.dataset.motion = String(this.motion);
      root.dataset.single = String(items.length < 2);

      this.io = new IntersectionObserver(
        (entries) => {
          this.visible = entries[0].isIntersecting;
          this.syncPlaying();
        },
        { threshold: 0.2 }
      );
      this.io.observe(root);

      this.onVisibility = () => {
        this.tabVisible = !document.hidden;
        this.syncPlaying();
      };
      document.addEventListener('visibilitychange', this.onVisibility);

      this.onMotionChange = () => {
        this.motion = !reduced.matches;
        root.dataset.motion = String(this.motion);
        this.syncPlaying();
      };
      reduced.addEventListener?.('change', this.onMotionChange);

      // Pause only for keyboard focus (:focus-visible); a mouse click on a control must not stop the slideshow.
      const syncFocus = () => {
        this.focused = Boolean(root.querySelector(':focus-visible'));
        this.syncPlaying();
      };
      root.addEventListener('focusin', syncFocus);
      root.addEventListener('focusout', () => setTimeout(syncFocus, 0));

      progress?.addEventListener('animationend', () => {
        if (this.isPlaying()) this.step(1);
      });

      this.syncPlaying();
    }

    teardownRotator() {
      this.io?.disconnect();
      document.removeEventListener('visibilitychange', this.onVisibility);
      reduced.removeEventListener?.('change', this.onMotionChange);
      this.timers?.forEach((t) => clearTimeout(t));
    }

    later(fn, ms) {
      const id = setTimeout(() => {
        this.timers.delete(id);
        fn();
      }, ms);
      this.timers.add(id);
    }

    isPlaying() {
      return this.items.length > 1 && this.motion && this.visible && this.tabVisible && !this.userPaused && !this.focused;
    }

    syncPlaying() {
      const playing = this.isPlaying();
      this.root.dataset.playing = String(playing);
      const live = this.querySelector('[data-live]');
      if (live) live.setAttribute('aria-live', playing ? 'off' : 'polite');
    }

    restartProgress() {
      if (!this.progress) return;
      this.progress.style.animation = 'none';
      void this.progress.offsetWidth;
      this.progress.style.animation = '';
    }

    togglePause(button) {
      this.userPaused = !this.userPaused;
      this.root.dataset.userPaused = String(this.userPaused);
      button?.setAttribute('aria-pressed', String(this.userPaused));
      button?.setAttribute('aria-label', this.userPaused ? button.dataset.labelPlay : button.dataset.labelPause);
      this.syncPlaying();
    }

    step(delta) {
      this.goTo(this.index + delta, delta < 0 ? 'prev' : 'next');
    }

    goTo(target, dir = 'next') {
      const n = this.items.length;
      const to = ((target % n) + n) % n;
      if (to === this.index) return;
      this.finishTransition();
      this.transition(this.index, to, dir);
      this.index = to;
      this.restartProgress();
      this.onChange?.(to);
    }

    finishTransition() {
      this.timers.forEach((t) => clearTimeout(t));
      this.timers.clear();
      this.items.forEach((el) => {
        if (el.dataset.state === 'leaving' || el.dataset.state === 'entering') {
          el.dataset.state = 'idle';
        }
      });
    }
  }

  class AgroleyHero extends Rotator {
    connectedCallback() {
      const root = this.querySelector('.ag-hero');
      if (!root) return;
      const interval = Number(root.dataset.interval) || 5;
      root.style.setProperty('--ag-interval', `${interval}s`);
      const scenes = [...this.querySelectorAll('.ag-scene')];
      this.dots = [...this.querySelectorAll('[data-hero-dot]')];
      this.pauseButton = this.querySelector('[data-hero-pause]');
      this.status = this.querySelector('[data-hero-status]');

      scenes.forEach((scene, i) => {
        if (i !== 0) scene.setAttribute('inert', '');
      });

      this.setupRotator({
        root,
        items: scenes,
        progress: this.querySelector('.ag-hero__progress span'),
        onChange: (i) => this.update(i),
      });

      this.querySelector('[data-hero-prev]')?.addEventListener('click', () => this.step(-1));
      this.querySelector('[data-hero-next]')?.addEventListener('click', () => this.step(1));
      this.dots.forEach((dot, i) =>
        dot.addEventListener('click', () => this.goTo(i, i < this.index ? 'prev' : 'next'))
      );
      this.pauseButton?.addEventListener('click', () => this.togglePause(this.pauseButton));
      if (root.dataset.autoplay === 'false') this.togglePause(this.pauseButton);
    }

    disconnectedCallback() {
      this.teardownRotator?.();
    }

    transition(from, to, dir) {
      const out = this.items[from];
      const inn = this.items[to];
      this.root.dataset.dir = dir;
      inn.removeAttribute('inert');
      out.setAttribute('inert', '');
      if (!this.motion) {
        out.dataset.state = 'idle';
        inn.dataset.state = 'active';
        return;
      }
      inn.dataset.state = 'entering';
      void inn.offsetWidth;
      inn.dataset.state = 'active';
      out.dataset.state = 'leaving';
      this.later(() => {
        if (out.dataset.state === 'leaving') out.dataset.state = 'idle';
      }, TRAVEL_MS);
    }

    update(i) {
      this.dots.forEach((dot, d) => dot.setAttribute('aria-current', String(d === i)));
      if (this.status) {
        const label = this.items[i].getAttribute('aria-label');
        this.status.textContent = label || '';
      }
    }
  }

  class AgroleyRange extends Rotator {
    connectedCallback() {
      const root = this.querySelector('.ag-range');
      if (!root) return;
      const interval = Number(root.dataset.interval) || 4.5;
      root.style.setProperty('--ag-interval', `${interval}s`);
      this.layers = [...this.querySelectorAll('.ag-range__layer')];
      this.panels = [...this.querySelectorAll('.ag-range__panel')];
      this.thumbs = [...this.querySelectorAll('[data-range-thumb]')];
      this.counter = this.querySelector('[data-range-current]');
      this.pauseButton = this.querySelector('[data-range-pause]');

      this.setupRotator({
        root,
        items: this.layers,
        progress: this.querySelector('.ag-range__timing span'),
        onChange: (i) => this.update(i),
      });

      this.layers.forEach((layer, i) => {
        if (i !== this.index) layer.setAttribute('tabindex', '-1');
      });
      this.querySelector('[data-range-prev]')?.addEventListener('click', () => this.step(-1));
      this.querySelector('[data-range-next]')?.addEventListener('click', () => this.step(1));
      this.thumbs.forEach((thumb, i) => thumb.addEventListener('click', () => this.goTo(i, i < this.index ? 'prev' : 'next')));
      this.pauseButton?.addEventListener('click', () => this.togglePause(this.pauseButton));
    }

    disconnectedCallback() {
      this.teardownRotator?.();
    }

    transition(from, to) {
      const out = this.layers[from];
      const inn = this.layers[to];
      out.setAttribute('tabindex', '-1');
      inn.removeAttribute('tabindex');
      if (!this.motion) {
        out.dataset.state = 'idle';
        inn.dataset.state = 'active';
        return;
      }
      out.dataset.state = 'leaving';
      inn.dataset.state = 'active';
      this.later(() => {
        if (out.dataset.state === 'leaving') out.dataset.state = 'idle';
      }, FADE_MS);
    }

    update(i) {
      this.panels.forEach((panel, p) => {
        panel.dataset.state = p === i ? 'active' : 'idle';
      });
      this.thumbs.forEach((thumb, t) => thumb.setAttribute('aria-pressed', String(t === i)));
      if (this.counter) this.counter.textContent = String(i + 1).padStart(2, '0');
      const rail = this.querySelector('.ag-range__rail');
      const thumb = this.thumbs[i];
      if (rail && thumb && rail.scrollWidth > rail.clientWidth) {
        rail.scrollTo({ left: thumb.offsetLeft - (rail.clientWidth - thumb.offsetWidth) / 2, behavior: this.motion ? 'smooth' : 'auto' });
      }
    }
  }

  /* Collection filter/sort form: submit on change (the form still works without JavaScript). */
  class AgroleyFilters extends HTMLElement {
    connectedCallback() {
      const form = this.querySelector('form[data-filter-form]');
      if (!form) return;
      const menus = () => Array.from(this.querySelectorAll('details[data-filter]'));
      const closeAll = (except) => menus().forEach((d) => { if (d !== except) d.open = false; });

      // Checkbox and sort changes apply at once; price waits for the Apply button or Enter.
      // Sort options are radio buttons: arrow keys only move the choice (no reload); click, Space or Enter applies it.
      let arrowing = false;
      this.addEventListener('keydown', (event) => {
        if (!event.target.matches('[data-sort]')) return;
        if (event.key.startsWith('Arrow')) arrowing = true;
        else if (event.key === 'Enter') form.requestSubmit();
      });
      this.addEventListener('keyup', () => { arrowing = false; });
      form.addEventListener('change', (event) => {
        if (event.target.matches('[data-price]')) return;
        if (event.target.matches('[data-sort]') && arrowing) return;
        form.requestSubmit();
      });

      // Price: a reversed range is swapped instead of returning no products.
      form.addEventListener('submit', () => {
        const [min, max] = form.querySelectorAll('[data-price]');
        if (min && max && min.value !== '' && max.value !== '' && Number(min.value) > Number(max.value)) {
          [min.value, max.value] = [max.value, min.value];
        }
        // Do not send empty price fields, which would leave blank parameters in the URL.
        form.querySelectorAll('[data-price]').forEach((i) => { if (i.value === '') i.disabled = true; });
      });

      // One menu open at a time.
      this.addEventListener('toggle', (event) => {
        if (event.target.matches('details[data-filter]') && event.target.open) closeAll(event.target);
      }, true);

      // Click or tap outside closes the menu.
      this.outside = (event) => {
        if (!this.contains(event.target) || !event.target.closest('details[data-filter]')) {
          closeAll();
        } else {
          closeAll(event.target.closest('details[data-filter]'));
        }
      };
      document.addEventListener('click', this.outside);

      // Escape closes and returns focus to the button; Tab out of the menu closes it.
      this.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape') return;
        const open = menus().find((d) => d.open);
        if (!open) return;
        open.open = false;
        const summary = open.querySelector('summary');
        if (summary) summary.focus();
      });
      this.addEventListener('focusout', (event) => {
        const d = event.target.closest && event.target.closest('details[data-filter]');
        if (d && d.open && event.relatedTarget && !d.contains(event.relatedTarget)) d.open = false;
      });
    }

    disconnectedCallback() {
      if (this.outside) document.removeEventListener('click', this.outside);
    }
  }


  /* Seed-to-puff sequence (AGRO-071). Four stages with their own dwell times (ms). Autoplay runs only
     while the image has loaded, the sequence is at least 25% on screen, the tab is visible and the visitor
     has not picked a stage. Reduced motion: no autoplay, the finished puff is shown first. */
  class AgroleyPop extends HTMLElement {
    connectedCallback() {
      this.durations = [2000, 2000, 1400, 3600];
      this.stage = 0;
      this.playing = true;
      this.visible = false;
      this.ready = false;
      this.pageVisible = !document.hidden;
      this.timer = 0;
      this.buttons = Array.from(this.querySelectorAll('[data-stage-button]'));
      this.titleEl = this.querySelector('[data-pop-title]');
      this.captionEl = this.querySelector('[data-pop-caption]');
      const list = this.querySelector('.ag-pop__stages');
      if (list) list.hidden = false; // the buttons only make sense once this script runs

      this.buttons.forEach((button) => {
        button.addEventListener('click', () => {
          this.playing = false;
          this.setStage(Number(button.dataset.stageButton));
          this.sync();
        });
      });

      this.onMotion = () => {
        if (reduced.matches) {
          this.playing = false;
          this.setStage(this.buttons.length ? Math.min(3, this.buttons.length - 1) : 3);
        }
        this.sync();
      };
      reduced.addEventListener?.('change', this.onMotion);

      this.onVisibility = () => {
        this.pageVisible = !document.hidden;
        this.sync();
      };
      document.addEventListener('visibilitychange', this.onVisibility);

      if ('IntersectionObserver' in window) {
        this.io = new IntersectionObserver(
          (entries) => {
            this.visible = entries[0].isIntersecting;
            this.sync();
          },
          { threshold: 0.25 }
        );
        this.io.observe(this);
      } else {
        this.visible = true;
      }

      if (reduced.matches) {
        this.playing = false;
        this.setStage(Math.min(3, Math.max(0, this.buttons.length - 1)));
      }

      const src = this.dataset.sprite;
      if (src) {
        const img = new Image();
        img.onload = () => {
          this.ready = true;
          this.sync();
        };
        img.onerror = () => {
          this.playing = false;
          const fallback = this.querySelector('.ag-pop__fallback');
          if (fallback) fallback.hidden = false;
          this.classList.add('is-failed');
          this.sync();
        };
        img.src = src;
      }
    }

    disconnectedCallback() {
      clearTimeout(this.timer);
      this.io?.disconnect();
      reduced.removeEventListener?.('change', this.onMotion);
      document.removeEventListener('visibilitychange', this.onVisibility);
    }

    get running() {
      return this.playing && this.visible && this.pageVisible && this.ready && !reduced.matches;
    }

    setStage(index) {
      const button = this.buttons[index];
      this.stage = index;
      this.dataset.stage = String(index);
      this.buttons.forEach((b, i) => b.setAttribute('aria-pressed', String(i === index)));
      if (button) {
        if (this.titleEl) this.titleEl.textContent = button.dataset.title || '';
        if (this.captionEl) this.captionEl.textContent = button.dataset.caption || '';
      }
    }

    sync() {
      clearTimeout(this.timer);
      this.dataset.running = String(this.running);
      if (!this.running || !this.buttons.length) return;
      this.timer = setTimeout(() => {
        this.setStage((this.stage + 1) % Math.min(4, this.buttons.length));
        this.sync();
      }, this.durations[this.stage] || 2000);
    }
  }

  const define = (name, ctor) => {
    if (!customElements.get(name)) customElements.define(name, ctor);
  };
  define('agroley-section', AgroleySection);
  define('agroley-strip', AgroleyStrip);
  define('agroley-hero', AgroleyHero);
  define('agroley-range', AgroleyRange);
  define('agroley-filters', AgroleyFilters);
  define('agroley-pop', AgroleyPop);
})();
